/**
 * Solo sauna PDP: per-variant prices on block swatches + price row updates.
 */
(function () {
    function formatMoney(cents) {
      const format =
        window.theme?.moneyFormat ||
        (typeof Shopify !== 'undefined' && Shopify.money_format) ||
        '${{amount}}';
  
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, format);
      }
  
      return '$' + (cents / 100).toFixed(2);
    }
  
    function initSoloGallery(section) {
      const gallery = section.querySelector('.product-gallery--solo-sauna');
      if (!gallery) return;

      const carousel = gallery.querySelector('media-carousel');
      const pagination = gallery.querySelector('[data-solo-sauna-gallery-pagination]');
      if (!carousel || !pagination) return;

      const dots = Array.from(pagination.querySelectorAll('.solo-sauna-gallery-pagination__dot'));
      if (dots.length < 2) {
        pagination.hidden = true;
        return;
      }

      pagination.hidden = false;

      function syncDots(selectedIndex) {
        dots.forEach((dot) => {
          const index = parseInt(dot.getAttribute('data-carousel-index'), 10);
          dot.setAttribute('aria-current', index === selectedIndex ? 'true' : 'false');
        });
      }

      function applyFilter(filteredIndexes) {
        let visible = 0;
        dots.forEach((dot) => {
          const index = parseInt(dot.getAttribute('data-carousel-index'), 10);
          const hidden = Array.isArray(filteredIndexes) && filteredIndexes.includes(index);
          dot.hidden = hidden;
          if (!hidden) visible += 1;
        });
        pagination.hidden = visible < 2;
      }

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.getAttribute('data-carousel-index'), 10);
          if (!Number.isNaN(index)) {
            carousel.select(index, { animate: true });
          }
        });
      });

      carousel.addEventListener('carousel:change', (event) => {
        syncDots(event.detail.index);
      });

      carousel.addEventListener('carousel:filter', (event) => {
        applyFilter(event.detail.filteredIndexes);
      });

      syncDots(carousel.selectedIndex);
    }

    function promoDisplayPrice(catalogPriceCents, priceList) {
      if (!priceList) return catalogPriceCents;

      const promoPercent = Number(priceList.getAttribute('data-promo-percent') || 0);
      const promoAmountCents = Number(priceList.getAttribute('data-promo-amount-cents') || 0);

      if (promoPercent > 0 && promoPercent < 100) {
        return Math.round(catalogPriceCents * (100 - promoPercent) / 100);
      }

      if (promoAmountCents > 0) {
        return Math.max(0, catalogPriceCents - promoAmountCents);
      }

      return catalogPriceCents;
    }

    function promoSaleState(variant, priceList) {
      const catalogPrice = Number(variant?.price) || 0;
      const displayPrice = promoDisplayPrice(catalogPrice, priceList);
      const nativeCompare = Number(variant?.compare_at_price) || 0;
      const promoActive = displayPrice < catalogPrice;
      const compareAt = promoActive ? Math.max(nativeCompare, catalogPrice) : nativeCompare;
      return {
        displayPrice,
        compareAt,
        onSale: compareAt > displayPrice
      };
    }

    function priceListHasPromo(priceList) {
      if (!priceList) return false;
      return (
        Number(priceList.getAttribute('data-promo-percent') || 0) > 0 ||
        Number(priceList.getAttribute('data-promo-amount-cents') || 0) > 0
      );
    }

    function setPriceElementMoney(el, cents) {
      if (!el) return;
      const moneyNodes = document.createRange().createContextualFragment(formatMoney(cents));
      const srOnly = el.querySelector(':scope > .sr-only');
      el.replaceChildren(...(srOnly ? [srOnly] : []), ...Array.from(moneyNodes.childNodes));
    }

    /**
     * Regios DOPP hides compare-at and resets sale to catalog when Shopify
     * compare_at_price is blank or equals price (metaobject-only promos).
     * Strip its hide markers so Promotion sale/compare can stay visible.
     */
    function clearRegiosPriceHide(el) {
      if (!el) return;
      el.hidden = false;
      el.removeAttribute('hidden');
      el.removeAttribute('aria-hidden');
      el.removeAttribute('data-hidden-by-regios-dopp');
      el.removeAttribute('data-hide');
      el.classList.remove('hide', 'hidden', 'visually-hidden');
      if (el.style) {
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
      }
    }

    function ensurePromoCompareAt(priceList, formId) {
      if (!priceList) return null;
      let compareEl = priceList.querySelector('compare-at-price');
      if (compareEl) return compareEl;

      compareEl = document.createElement('compare-at-price');
      if (formId) compareEl.setAttribute('form', formId);
      compareEl.className = 'text-subdued line-through';
      const saleEl = priceList.querySelector('sale-price');
      if (saleEl) {
        saleEl.insertAdjacentElement('afterend', compareEl);
      } else {
        priceList.appendChild(compareEl);
      }
      return compareEl;
    }

    function ensurePromoBadge(priceList, formId) {
      if (!priceList) return null;
      let badgeEl = priceList.querySelector('on-sale-badge');
      if (badgeEl) return badgeEl;

      badgeEl = document.createElement('on-sale-badge');
      if (formId) badgeEl.setAttribute('form', formId);
      badgeEl.className = 'badge badge--on-sale';
      const discountMode = window.themeVariables?.settings?.discountMode;
      if (discountMode) badgeEl.setAttribute('discount-mode', discountMode);
      const promoPercent = priceList.getAttribute('data-promo-percent');
      const promoAmount = priceList.getAttribute('data-promo-amount-cents');
      if (promoPercent) badgeEl.setAttribute('data-promo-percent', promoPercent);
      if (promoAmount) badgeEl.setAttribute('data-promo-amount-cents', promoAmount);
      priceList.appendChild(badgeEl);
      return badgeEl;
    }

    function updateSoloSaunaPrice(section, variant) {
      const priceHost = section.querySelector('[data-solo-sauna-price]');
      if (!priceHost || !variant) return;

      const priceList = priceHost.querySelector('price-list');
      const formId = section.querySelector('form[is="product-form"]')?.id;
      const { displayPrice, compareAt, onSale } = promoSaleState(variant, priceList);
      const saleEl = priceHost.querySelector('sale-price');
      let compareEl = priceHost.querySelector('compare-at-price');
      let badgeEl = priceHost.querySelector('on-sale-badge');

      if (onSale) {
        compareEl = ensurePromoCompareAt(priceList, formId) || compareEl;
        badgeEl = ensurePromoBadge(priceList, formId) || badgeEl;
      }

      if (saleEl) {
        setPriceElementMoney(saleEl, displayPrice);
        saleEl.classList.toggle('text-on-sale', onSale);
        saleEl.classList.toggle('bold', onSale);
        if (!onSale) saleEl.classList.add('text-subdued');
        else saleEl.classList.remove('text-subdued');
      } else {
        const fallback = priceHost.querySelector('.text-on-sale, .text-lg, .price-list');
        if (fallback) {
          fallback.textContent = formatMoney(displayPrice);
        }
      }

      if (compareEl) {
        setPriceElementMoney(compareEl, compareAt || displayPrice);
        if (onSale) {
          clearRegiosPriceHide(compareEl);
          compareEl.classList.add('text-subdued', 'line-through');
        } else {
          compareEl.hidden = true;
        }
      }

      if (badgeEl) {
        if (onSale) {
          clearRegiosPriceHide(badgeEl);
          if (badgeEl.hasAttribute('discount-mode')) {
            const savings = badgeEl.getAttribute('discount-mode') === 'percentage'
              ? `${Math.round((compareAt - displayPrice) * 100 / compareAt)}%`
              : formatMoney(compareAt - displayPrice);
            const template = window.themeVariables?.strings?.discountBadge;
            if (template) {
              badgeEl.innerHTML = template.replace('@@', savings);
            }
          }
        } else {
          badgeEl.hidden = true;
        }
      }
    }

    function promoPricingLooksBroken(priceHost) {
      const priceList = priceHost.querySelector('price-list');
      if (!priceListHasPromo(priceList)) return false;

      const compareEl = priceHost.querySelector('compare-at-price');
      // Regios often removes or fully hides compare-at when Shopify has no compare-at.
      if (!compareEl) return true;
      if (
        compareEl.hasAttribute('data-hidden-by-regios-dopp') ||
        compareEl.hasAttribute('hidden') ||
        compareEl.hidden ||
        compareEl.classList.contains('visually-hidden') ||
        compareEl.classList.contains('hide') ||
        compareEl.getAttribute('aria-hidden') === 'true'
      ) {
        return true;
      }

      try {
        if (window.getComputedStyle(compareEl).display === 'none') return true;
      } catch (e) {
        // Ignore environments without layout.
      }

      const saleEl = priceHost.querySelector('sale-price');
      if (saleEl && !saleEl.classList.contains('text-on-sale')) return true;

      return false;
    }

    async function resolveSectionVariant(section) {
      const picker = section.querySelector('variant-picker');
      if (picker?.selectedVariant) return picker.selectedVariant;

      const form = section.querySelector('form[is="product-form"]');
      const variantId = form?.querySelector('[name="id"]')?.value;
      const handle =
        picker?.getAttribute('handle') ||
        section.querySelector('[data-product-handle]')?.getAttribute('data-product-handle') ||
        window.meta?.product?.handle;
      if (!handle || !variantId) return null;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}products/${handle}.js`);
        if (!response.ok) return null;
        const product = await response.json();
        return product.variants.find((entry) => String(entry.id) === String(variantId)) || null;
      } catch (e) {
        return null;
      }
    }

    /**
     * Re-apply Promotion metaobject prices after Regios DOPP clobbers them.
     * Always reassert while a promo price-list is present — Regios can reset the
     * sale amount to catalog without leaving hide markers when compare-at is blank.
     */
    function guardPromoPricing(section) {
      const priceHost = section.querySelector('[data-solo-sauna-price]');
      if (!priceHost || priceHost.dataset.promoRegiosGuard === 'true') return;
      if (!priceListHasPromo(priceHost.querySelector('price-list'))) return;

      priceHost.dataset.promoRegiosGuard = 'true';
      let reasserting = false;

      const reassert = async (force = false) => {
        if (reasserting) return;
        if (!force && !promoPricingLooksBroken(priceHost)) return;
        reasserting = true;
        try {
          const variant = await resolveSectionVariant(section);
          if (variant) updateSoloSaunaPrice(section, variant);
        } finally {
          requestAnimationFrame(() => {
            reasserting = false;
          });
        }
      };

      new MutationObserver(() => {
        reassert(true);
      }).observe(priceHost, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['hidden', 'class', 'style', 'data-hidden-by-regios-dopp', 'aria-hidden']
      });

      [0, 150, 400, 800, 1500, 3000, 5000, 8000].forEach((delay) => {
        window.setTimeout(() => reassert(true), delay);
      });
    }

    function initSoloPriceUpdate(section) {
      const priceHost = section.querySelector('[data-solo-sauna-price]');
      const form = section.querySelector('form[is="product-form"]');
      if (!priceHost || !form || form.dataset.soloSaunaPriceBound === 'true') return;

      form.dataset.soloSaunaPriceBound = 'true';
      form.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;
        if (variant) {
          updateSoloSaunaPrice(section, variant);
        }
      });

      guardPromoPricing(section);

      const initialVariantId = form.querySelector('[name="id"]')?.value;
      const variantsScript = section.querySelector('[data-solo-sauna-variants]');
      if (initialVariantId && variantsScript) {
        try {
          const variants = JSON.parse(variantsScript.textContent);
          const initialVariant = variants.find((entry) => String(entry.id) === String(initialVariantId));
          if (initialVariant) {
            updateSoloSaunaPrice(section, initialVariant);
          }
        } catch (e) {
          // Ignore malformed variant JSON.
        }
      } else {
        resolveSectionVariant(section).then((variant) => {
          if (variant) updateSoloSaunaPrice(section, variant);
        });
      }
    }

    function initSection(section) {
      initSoloGallery(section);

      const formId = section.querySelector('form[is="product-form"]')?.id;
      if (!formId) return;
  
      const form = document.getElementById(formId);
      const variantsScript = section.querySelector('[data-solo-sauna-variants]');
      if (!form || !variantsScript) return;
  
      let variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        return;
      }
  
      function decorateVariantCards() {
        section.querySelectorAll('.solo-sauna-variant-picker .block-swatch').forEach((label) => {
          if (label.querySelector('[data-solo-sauna-variant-price]')) return;
  
          const input =
            label.previousElementSibling?.matches?.('input[type="radio"]') &&
            label.previousElementSibling.name?.startsWith('option')
              ? label.previousElementSibling
              : null;
  
          if (!input) return;
  
          const optionIndex = parseInt(input.name.replace('option', ''), 10) - 1;
          const match = variants.find((v) => v.options[optionIndex] === input.value);
          if (!match) return;
  
          const priceEl = document.createElement('span');
          priceEl.className = 'solo-sauna-variant-card__price';
          priceEl.setAttribute('data-solo-sauna-variant-price', '');
          priceEl.textContent = formatMoney(match.price);
          label.appendChild(priceEl);
        });
      }
  
      decorateVariantCards();
    }

    function initSoloPricing(section) {
      const form = section.querySelector('form[is="product-form"]');
      const shippingValuesScript = section.querySelector('[data-solo-sauna-free-shipping-values]');
      if (!form || !shippingValuesScript) return;

      let shippingRowByVariantId = {};
      const gateFreeShipping = Boolean(
        shippingValuesScript.closest('[data-gate-free-shipping]')
      );

      try {
        const shippingRows = JSON.parse(shippingValuesScript.textContent);
        shippingRowByVariantId = Object.fromEntries(
          shippingRows.map((row) => [String(row.id), row])
        );
      } catch (e) {
        return;
      }

      function updateShippingRow(variant) {
        const badgeEl = section.querySelector('[data-solo-sauna-shipping-badge]');
        const valueEl = section.querySelector('[data-solo-sauna-shipping-value]');
        if (!variant) return;

        const row = shippingRowByVariantId[String(variant.id)] || {};

        if (badgeEl && gateFreeShipping) {
          badgeEl.hidden = row.showBadge !== true;
        }

        if (!valueEl) return;

        const label = row.label || '';

        if (label) {
          valueEl.textContent = label;
          valueEl.hidden = false;
        } else {
          valueEl.textContent = '';
          valueEl.hidden = true;
        }
      }

      form.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;
        if (variant) {
          updateShippingRow(variant);
        }
      });
    }

    function trustpilotWidgetHasReviews(widget) {
      const iframe = widget.querySelector('iframe');

      if (iframe) {
        const height = Math.max(
          iframe.getBoundingClientRect().height,
          iframe.offsetHeight
        );

        if (height > 8) {
          return true;
        }
      }

      return false;
    }

    function hideReviewSection(block) {
      block.hidden = true;

      const shopifySection = block.closest('.shopify-section');
      if (shopifySection) {
        shopifySection.hidden = true;
      }
    }

    function revealReviewSection(block) {
      block.hidden = false;

      const shopifySection = block.closest('.shopify-section');
      if (shopifySection) {
        shopifySection.hidden = false;
      }
    }

    function initReviewSections(root) {
      root.querySelectorAll('[data-reviews-section]').forEach((block) => {
        const widgets = block.querySelectorAll('.trustpilot-widget');
        if (!widgets.length) {
          revealReviewSection(block);
          return;
        }

        revealReviewSection(block);

        const checkSection = () =>
          Array.from(widgets).some((widget) => trustpilotWidgetHasReviews(widget));

        [500, 1500, 3000, 5000, 8000, 12000].forEach((delay) => {
          window.setTimeout(() => {
            if (!checkSection() && delay === 12000) {
              hideReviewSection(block);
            }
          }, delay);
        });
      });
    }

    function initSoloReviewsSection(section) {
      if (!section.classList.contains('shopify-section--solo-reviews')) return;

      const embed = section.querySelector('.solo-reviews__embed');
      if (!embed) {
        section.hidden = true;
        return;
      }

      const iframe = embed.querySelector('iframe');
      if (!iframe) {
        section.hidden = true;
        return;
      }

      const hideIfEmpty = () => {
        const height = Math.max(
          iframe.getBoundingClientRect().height,
          iframe.offsetHeight,
          embed.getBoundingClientRect().height
        );

        if (height < 48) {
          section.hidden = true;
        }
      };

      iframe.addEventListener('load', () => {
        window.setTimeout(hideIfEmpty, 800);
        window.setTimeout(hideIfEmpty, 2500);
      });

      window.setTimeout(hideIfEmpty, 3000);
      window.setTimeout(hideIfEmpty, 6000);
    }

    function init() {
      initReviewSections(document);

      document.querySelectorAll('.shopify-section--main-product').forEach((section) => {
        initSoloGallery(section);
        initSoloPriceUpdate(section);
        if (!section.querySelector('.solo-sauna-pdp')) return;
        initSection(section);
        initSoloPricing(section);
      });

      document.querySelectorAll('.shopify-section--solo-reviews').forEach(initSoloReviewsSection);
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  
    document.addEventListener('shopify:section:load', (event) => {
      initReviewSections(event.target);

      if (event.target.classList.contains('shopify-section--main-product')) {
        initSoloGallery(event.target);
        initSoloPriceUpdate(event.target);
        if (event.target.querySelector('.solo-sauna-pdp')) {
          initSection(event.target);
          initSoloPricing(event.target);
        }
      }

      if (event.target.classList.contains('shopify-section--solo-reviews')) {
        initSoloReviewsSection(event.target);
      }
    });
  })();
  